import { Connector, useDisconnect } from "wagmi";
import ModalBackButton from "./../components/ModalBackButton";
import SecondaryButton from "./../components/SecondaryButton";
import { ReactNode, useState, useEffect } from "react";
import { useGlideDepositConfig } from "../lib/useDepositConfig";
import { useConnectedWallet } from "../lib/useConnectedWallet";
import { useWallets as useEVMWallets } from "../lib/connectkit/wallets/useWallets";
import { useConnect } from "../lib/connectkit/hooks/useConnect";
import TransactionCheckoutHeader from "../components/TransactionCheckoutHeader";
import { SectionTitle } from "../components/SectionTitle";
import { solana, solanaDevnet } from "@paywithglide/glide-js/chains";
import { getWallets } from "@wallet-standard/app";
import type { Wallet } from "@wallet-standard/base";
import { connect as connectSolana } from "../lib/solana/connect";
import { LogoWithBadge } from "../components/LogoWithBadge";
import { glideLocalStorage } from "../lib/glideLocalStorage";
import { logger } from "../lib/logger";
import { formatShortWalletAddress } from "../lib/formatShortWalletAddress";
import { useDeviceContext } from "../lib/useDeviceContext";

type Props = {
  onConnect: () => void | Promise<unknown>;
  onBack?: () => void;
};

const SolanaWalletOption = ({
  wallet,
  onConnect,
}: {
  wallet: Wallet;
  onConnect: () => void | Promise<unknown>;
}) => {
  const [isConnecting, setIsConnecting] = useState(false);

  return (
    <SecondaryButton
      icon={<LogoWithBadge logoUrl={wallet.icon} badgeUrl={solana.logoUrl} />}
      title={wallet.name}
      accessory={
        <div className="text-[--color-text-secondary] px-1 text-xs">Solana</div>
      }
      onClick={async () => {
        try {
          setIsConnecting(true);
          await glideLocalStorage.setItem("glide.useExternalWallet", "true");
          await connectSolana({ wallet });
          await glideLocalStorage.setItem(
            "glide.externalWalletEcosystem",
            "solana",
          );

          await onConnect();
        } catch (error) {
          logger.error(`Failed to connect solana wallet: ${error}`);
        } finally {
          setIsConnecting(false);
        }
      }}
      loading={isConnecting}
    />
  );
};

const EVMWalletOption = ({
  connector,
  logo,
  name,
  onConnect,
}: {
  connector: Connector;
  logo: ReactNode;
  name: string;
  onConnect: () => void | Promise<unknown>;
}) => {
  const { connectAsync } = useConnect();
  const { disconnectAsync } = useDisconnect();

  const [isConnecting, setIsConnecting] = useState(false);

  const connect = async () => {
    setIsConnecting(true);

    try {
      await glideLocalStorage.setItem("glide.useExternalWallet", "true");
      await disconnectAsync();
      await connectAsync({ connector });
      await glideLocalStorage.setItem("glide.externalWalletEcosystem", "evm");

      await onConnect();
    } catch (e) {
      logger.error(`${e}`);
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <SecondaryButton
      icon={logo}
      title={name}
      onClick={() => connect()}
      loading={isConnecting}
    />
  );
};

export default function ConnectWallet({ onConnect, onBack }: Props) {
  const depositConfig = useGlideDepositConfig();
  const { hasParentAppWallet } = useConnectedWallet();
  const { isMobile } = useDeviceContext();

  const { disconnectAsync } = useDisconnect();

  useEffect(() => {
    const handleWalletConnected = () => onConnect();
    window.addEventListener("wallet-connected", handleWalletConnected);
    return () =>
      window.removeEventListener("wallet-connected", handleWalletConnected);
  }, [onConnect]);

  const showEVMWallets =
    !depositConfig.chainIds?.length ||
    depositConfig.chainIds?.filter((id) => id.startsWith("eip155:"))?.length;
  const showOnlyParentAndWalletConnect = isMobile && hasParentAppWallet;

  const rawEvmWallets = useEVMWallets();
  const evmWallets = rawEvmWallets
    .filter(
      (w) =>
        w.isInstalled || w.id === "walletConnect" || w.id === "metaMaskSDK",
    )
    .map((w) => ({
      id: w.id,
      name: w.shortName || w.name!,
      icon: w.icon,
      connector: w.connector,
    }));

  const browserEVMWallets = showEVMWallets
    ? evmWallets.filter((w) => {
        if (showOnlyParentAndWalletConnect) return false;

        return w.id !== "coinbaseWalletSDK" && w.id !== "walletConnect";
      })
    : [];

  const otherEVMWallets = showEVMWallets
    ? evmWallets
        .filter((w) => {
          if (showOnlyParentAndWalletConnect) {
            return w.id === "walletConnect";
          }

          return (
            w.id === "walletConnect" ||
            (!depositConfig.popupsBlocked && w.id === "coinbaseWalletSDK")
          );
        })
        .sort((a, b) => {
          // WalletConnect should be first
          if (a.id === "walletConnect") return -1;
          if (b.id === "walletConnect") return 1;
          return 0;
        })
    : [];

  const allWallets = getWallets().get();
  const solanaWallets = showOnlyParentAndWalletConnect
    ? []
    : depositConfig.chain?.isTestnet &&
        (depositConfig.excludeChainIds?.includes(solanaDevnet.id) ||
          (depositConfig.chainIds?.length &&
            !depositConfig.chainIds?.includes(solanaDevnet.id)))
      ? []
      : !depositConfig.chain?.isTestnet &&
          (depositConfig.excludeChainIds?.includes(solana.id) ||
            (depositConfig.chainIds?.length &&
              !depositConfig.chainIds?.includes(solana.id)))
        ? []
        : allWallets.filter((wallet) =>
            wallet.chains.some((chain) => chain.startsWith("solana:")),
          );

  const [isConnectingParentWallet, setIsConnectingParentWallet] =
    useState(false);

  const connectParentAppWallet = async () => {
    setIsConnectingParentWallet(true);
    await glideLocalStorage.removeItem("glide.useExternalWallet");
    await glideLocalStorage.removeItem("glide.externalWalletEcosystem");
    await glideLocalStorage.removeItem("glide.solana.connector");
    await glideLocalStorage.removeItem("glide.solana.account");
    await disconnectAsync();
    await glideLocalStorage.setItem("glide.useParentWallet", "true");
    onConnect();
  };

  return (
    <>
      {onBack ? <ModalBackButton onClick={() => onBack()} /> : null}

      <TransactionCheckoutHeader title="Connect wallet" />

      <div>
        <div className="space-y-2">
          {hasParentAppWallet ? (
            <SecondaryButton
              icon={
                <img
                  src={depositConfig.appFaviconUrl}
                  className="h-6 w-6 rounded"
                  alt={`${depositConfig.appName} logo`}
                />
              }
              title={`${depositConfig.appName} wallet`}
              accessory={
                depositConfig.connectedWalletAddress ? (
                  <div className="text-[--color-text-secondary] px-1 text-xs">
                    {formatShortWalletAddress(
                      depositConfig.connectedWalletAddress,
                    )}
                  </div>
                ) : null
              }
              onClick={() => connectParentAppWallet()}
              loading={isConnectingParentWallet}
            />
          ) : null}

          {browserEVMWallets.map((wallet) => (
            <EVMWalletOption
              key={wallet.id}
              logo={
                <div className="h-6 w-6 overflow-hidden rounded [&>img]:block [&>img]:relative [&>img]:pointer-events-none [&>img]:overflow-hidden [&>img]:w-full [&>img]:h-full [&>svg]:block [&>svg]:relative [&>svg]:pointer-events-none [&>svg]:overflow-hidden [&>svg]:w-full [&>svg]:h-full">
                  {wallet.icon}
                </div>
              }
              name={wallet.name}
              connector={wallet.connector}
              onConnect={onConnect}
            />
          ))}

          {solanaWallets.map((wallet) => (
            <SolanaWalletOption
              key={wallet.name}
              wallet={wallet}
              onConnect={onConnect}
            />
          ))}
        </div>

        {(hasParentAppWallet ||
          browserEVMWallets.length ||
          solanaWallets.length) &&
        otherEVMWallets.length ? (
          <div className="mt-4">
            <SectionTitle title="Other wallets" />
          </div>
        ) : null}
        {otherEVMWallets.map((wallet) => (
          <EVMWalletOption
            key={wallet.id}
            logo={
              <div className="h-6 w-6 overflow-hidden rounded [&>img]:block [&>img]:relative [&>img]:pointer-events-none [&>img]:overflow-hidden [&>img]:w-full [&>img]:h-full [&>svg]:block [&>svg]:relative [&>svg]:pointer-events-none [&>svg]:overflow-hidden [&>svg]:w-full [&>svg]:h-full">
                {wallet.icon}
              </div>
            }
            name={wallet.name}
            connector={wallet.connector}
            onConnect={onConnect}
          />
        ))}
      </div>
    </>
  );
}
